import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Language } from '../../entities/language';
import { Translation } from '../../entities/translation';
import { strings } from '../../localization/strings';

interface TranslationCellProps {
  value: string | null | undefined;
  onSave: (value: string) => Promise<void>;
}

function TranslationCell({ value, onSave }: TranslationCellProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraft(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const save = async () => {
    if (draft !== (value || '')) {
      await onSave(draft);
    }

    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex min-h-[32px] items-start px-3 py-1">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => save()}
          style={{ backgroundColor: '#ffff66' }}
          className="w-full border-0 p-0 text-sm text-gray-900 outline-none focus:ring-0"
        />
      </div>
    );
  }

  return (
    <div className="min-h-[32px] px-3 py-1">
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className={`block w-full text-left text-sm ${value ? 'text-gray-900' : 'text-red-600'}`}
      >
        {value || strings.translations.missing}
      </button>
    </div>
  );
}

interface ComponentProps {
  translations: Translation[];
  languages: Language[];
  onSave: (translation: Translation, language: Language, value: string) => Promise<void>;
}

export default function TranslationsTableComponent({ translations, languages, onSave }: ComponentProps) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {languages.map((language) => (
              <th
                key={language.id}
                scope="col"
                className="whitespace-nowrap px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
              >
                {language.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {translations.map((translation) => (
            <tr key={translation.id}>
              {languages.map((language, index) => (
                <td key={language.id} className="p-0 align-top">
                  {index === 0 && (
                    <div className="h-5 truncate px-3 pt-2 text-xs text-gray-500" title={translation.key}>
                      <Link to={`/translations/edit/${translation.id}`} className="hover:text-indigo-600">
                        {translation.key}
                      </Link>
                    </div>
                  )}
                  {index > 0 && <div className="h-5" aria-hidden="true" />}
                  <TranslationCell
                    value={translation.text?.[language.key]}
                    onSave={(value) => onSave(translation, language, value)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
